import React from 'react';
import styled from "styled-components";

import PropSelector from './comps/PropSelector';
import EntryDetail from './aragon/EntryDetail';
import WeatherDetails from './aragon/WeatherDetails';
import WindAnalysis from './aragon/WindAnalysis';
import LandingView from './aragon/NewLandingView';
import FeedbackModal from './aragon/components/FeedbackModal';

import { Icon, Tabs, Typography, Button, COLORS } from '@virkesborsen/ui-component-library';
import { Chip } from './aragon/components/Chip';
import { Spinner } from 'react-bootstrap';

import { VisaValda, Navigation, PropsData } from './context';
import { useDFR } from './context/DFRContext';

const tabItems = ["Skogskador", "Risker", "I området", "Väder", "Vindanalys"]

const PropselectorContainer = styled.div`
    width: 90vw;
    max-width: 500px;
    top: 10px;
`

const ListPanel = styled.div`
    background-color: ${COLORS['stone-50']};
    border: 1px solid #E2E1E0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
`

const ListItem = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #e6ded5;
    border-left: 1px solid #e6ded5;
    border-right: 1px solid #e6ded5;

    &:hover {
        background-color: ${COLORS['charcoal-alpha-50']};
    }

    &.done {
        background-color: ${COLORS['cloud-100']};

        &:hover {
            background-color: ${COLORS['charcoal-alpha-50']};
        }
    }
`

const BottomTable = styled.div`
    padding: 16px;
    border-bottom: 1px solid #e6ded5;
    border-left: 1px solid #e6ded5;
    border-right: 1px solid #e6ded5;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;

    &.hover {
        &:hover {
            background-color: ${COLORS['charcoal-alpha-50']};
        }
    }
`

const RoundedContainer = styled.div`
    border-radius: 16px;
`

const FeedbackButton = styled.div`
    margin: 20px 20px 90px;
    @media (min-width: 768px) {
        margin: 20px;
    }
`

const Aragon = React.forwardRef((props, ref) => {
    const visaValdaContext = React.useContext(VisaValda);
    const navContext = React.useContext(Navigation);
    const propsContext = React.useContext(PropsData);

    const hasDFR = propsContext.props.some(p => p.dfr)

    const [activeTab, setActiveTab] = React.useState(navContext.view?.tab ? navContext.view?.tab : tabItems[0]);
    const [dataLength, setDataLength] = React.useState({Damage: 5, Risk: 5, News: 5});
    const [landingView, showLandingView] = React.useState(!propsContext.props.some(p => p.dfr && p.active));
    const [showFeedback, setShowFeedback] = React.useState(false);

    const DFRcontext = useDFR();

    React.useImperativeHandle(ref, () => ({
        handleHistory: () => {
            setActiveTab(history.state.tab ? history.state.tab : tabItems[0])
        }
    }))

    React.useEffect(() => {
        let view = navContext.view
        if(!view.tab) {
            view.tab = tabItems[0]
            history.replaceState(view, '', '');
        }
    }, []);

    React.useEffect(() => {
        if(visaValdaContext.visaValda) {
            showLandingView(!propsContext.props.some(p => p.dfr && p.active))
            setDataLength({Damage: 5, Risk: 5, News: 5})
            visaValdaContext.setVisaValda(false)
        }
    }, [visaValdaContext.visaValda]);
    
    const handleTabChange = tab => {
        setActiveTab(tab);
        let newView = {...navContext.view, tab: tab}
        history.pushState(newView, '', '')
        navContext.setView(newView)
    };

    const handleEntryChange = entry => {
        let newView = {...navContext.view, entry: entry}
        history.pushState(newView, '', '')
        navContext.setView(newView)
    }

    const ListPanelData = (title, description, key) => {
        const activeProps = propsContext.props.filter(p => p.active)
        const isLoading = activeProps.length != activeProps.filter(p => p.ready).length
        const items = DFRcontext.entries.filter(item => item.type == key && activeProps.some(p => p.ref == item.ref)).sort((a,b) => new Date(b.occured.slice(0, 10)) - new Date(a.occured.slice(0, 10)))

        return <>
            <Typography variant="subsectionHeaderSmallMedium">{title}</Typography>

            <Typography className='my-3' variant="introLeadMediumRegular">{description}</Typography>

            {isLoading ? <RoundedContainer className='d-flex flex-column justify-content-center align-items-center bg-stone-50' style={{minHeight: '300px'}}>
                <Spinner animation="border" role="status" size="lg" />
                <Typography variant='subheaderLargeMedium' color='cloud-900' className='mt-3'>Vi hämtar din data...</Typography>
            </RoundedContainer> : 
            items.length == 0 ? <RoundedContainer className='d-flex justify-content-center bg-stone-50 p-3 p-lg-5'>
                <div className='d-flex flex-column justify-content-center align-items-center' style={{maxWidth: '722px'}}>
                    <div className='d-flex align-items-center justify-content-center rounded rounded-circle bg-stone-100 mb-3' style={{height: '60px', width: '60px'}}>
                        <Icon name="PineTreeSimple" color='charcoal-500' size="xlarge" />
                    </div>
                    <Typography variant='subheaderLargeMedium' className='text-center mb-2'>Välkommen till skogsövervakning</Typography>

                    <Typography variant='bodyLargeRegular' className='text-center mb-3 mb-lg-5'>Det är är här du kommer kunna få viktigt information om din skogsfastighet. Allt från skogsskador, risker, händelser som inträffar i närområdet, väderinformation och vindanalys över din skogsfastighet.</Typography>

                    <RoundedContainer className='d-flex bg-white border border-1 border-pebble-50' style={{padding: '16px'}}>
                        <div style={{width: '32px'}}>
                            <div className='d-flex justify-content-center align-items-center rounded rounded-circle bg-green-spruce-50' style={{height: '32px', width: '32px'}}>
                                <Icon name='LightbulbSimple' color='green-spruce-700' size='large' />
                            </div>
                        </div>
                        
                        <div style={{marginLeft: '16px'}}>
                            <Typography variant='subheaderSmallMedium' className='mb-2'>Bra att  känna till!</Typography>

                            <Typography variant='bodyLargeRegular'>Vi har nu påbörjat övervakningen på din skogsfastighet. Eftersom tjänsten är ny för dig kan de första uppdateringarna dyka upp inom några timmar eller dagar, beroende på fastighetens läge och om något nytt inträffar i området.</Typography>
                        </div>
                    </RoundedContainer>
                </div>
            </RoundedContainer> : <>
                <div>
                    <ListPanel>
                        <Typography className='p-3' variant="labelLargeMedium">Aktivitet</Typography>
                    </ListPanel>
                    
                    {items.slice(0, dataLength[key]).map(item => {
                        const latestState = item.states.filter(s => !s.deleted)[0]
                        return <ListItem role='button' onClick={() => handleEntryChange(item)} className={`d-flex justify-content-between ${(latestState.state == 'done' || !latestState.state) && 'done'}`}>
                            <div className='d-flex flex-column'>
                                <Typography variant="labelMediumMedium">{item.title}</Typography>
                                <Typography variant="labelSmallRegular">{item.occured.slice(0, 10)}</Typography>
                            </div>

                            <div className='d-flex align-items-center'>
                                <Chip type={latestState.state} />
                                <Icon name="ChevronDownSimple" size="large" rotation={-90} />
                            </div>
                        </ListItem>
                    })}
                </div>
                {items.length > dataLength[key] ? <BottomTable role='button' className='d-flex justify-content-center hover' onClick={() => setDataLength({...dataLength, [key]: dataLength[key]+5})}>
                    <Typography className='me-2' variant="labelMediumMedium">Visa fler</Typography>
                    <Icon name="ChevronDownSimple" size="large" />
                </BottomTable> :
                <BottomTable className='text-center'>
                    <Typography variant="labelMediumMedium">Inga fler resultat</Typography>
                </BottomTable>}
            </>}
        </>
    }

    const activateDFRProps = () => {
        if(propsContext.props.some(p => p.dfr && p.active)) {
            showLandingView(false)
        } else {
            const newProps = propsContext.props.map(p => {
                if(p.dfr && p.ready) {
                    p.active = true
                    p.checked = true
                }
                return p
            })

            propsContext.setProps(newProps)
            visaValdaContext.setVisaValda(true)
        }
    }

    return <>
        {!navContext.view?.showMap && <FeedbackButton className='position-fixed bottom-0 end-0 z-1030 d-flex justify-content-end'>
            <Button variant='secondary' label='Lämna feedback' prefixIcon='EditPenSimple' onClick={() => setShowFeedback(true)} />
        </FeedbackButton>}
        
        {landingView && <div className='position-relative bg-cloud-200'>
            {hasDFR && <PropselectorContainer className='sticky-top z-1040 mx-auto'>
                <PropSelector />
            </PropselectorContainer>}
            
            {propsContext.props.filter(p => p.dfr && p.ready).length > 0 && <div className='container position-relative p-3' style={{maxWidth: '960px'}}>
                <div className='d-flex bg-white border border-1 border-pebble-50 mt-3' style={{borderRadius: '8px', padding: '12px'}}>
                    <div style={{marginRight: '12px', marginTop: '3px'}}>
                        <Icon name='InfoFilled' color='pebble-500' size='large' />
                    </div>
                    
                    <div>
                        <Typography variant='labelSmallMedium' className='mb-1'>Skogsövervakning finns inte för alla dom valda fastigheterna</Typography>

                        <Typography variant='bodyExtraSmallRegular' color='pebble-500'>Alla valda fastigheterna har inte tillgång till skogsövervakning. Men du har tillgång till skogsövervakning på dina andra fastigheter. Välj de fastigheter som har  <img className='d-inline-block' src={`${Window.static_path}img/icons/sparkle.svg`} alt='Sparkle' height='15px' width='15px' />  bredvid sig.</Typography>

                        {hasDFR && <Button label={propsContext.props.some(p => p.dfr && p.active) ? 'Tillbaka till skogsövervakning' : 'Se övervakade fastigheter'} className='mt-2' onClick={() => activateDFRProps()} />}
                    </div>
                </div>
            </div>}
            
            <LandingView />
        </div>}

        {!landingView && navContext.view.entry && <EntryDetail entry={navContext.view.entry} />}

        {!landingView && !navContext.view.entry && <div className='position-relative p-3' style={{maxWidth: '960px'}}>
            <PropselectorContainer className='sticky-top z-1040'>
                <PropSelector />
            </PropselectorContainer>

            {propsContext.props.filter(p => !p.dfr && p.active).length > 0 && <div className='d-flex bg-cloud-200 border border-1 border-pebble-50 mt-3' style={{borderRadius: '8px', padding: '12px'}}>
                <div style={{marginRight: '12px', marginTop: '3px'}}>
                    <Icon name='InfoFilled' color='pebble-500' size='large' />
                </div>
                
                <div>
                    <Typography variant='labelSmallMedium' className='mb-1'>{propsContext.props.filter(p => !p.dfr && p.active).length == 1 ? propsContext.props.filter(p => !p.dfr && p.active)[0].designation : 'Fler valda fastigheter'} har inte skogsövervakning aktiverat. </Typography>

                    <Typography variant='bodyExtraSmallRegular' color='pebble-500'>Aktivera tjänsten för att kunna följa förändringar och risker i området – precis som på dina andra fastigheter</Typography>

                    <div className='d-flex mt-2'>
                        <Button label='Köp skogsövervakning' onClick={() => DFRcontext.showCheckout(true)} />
                        <Button variant='secondary' label='Läs mer' className='ms-2' onClick={() => showLandingView(true)} />
                    </div>
                </div>
            </div>}

            <div className='mt-3'>
                <Tabs tabs={tabItems} onChange={(tab) => handleTabChange(tab)} activeTab={activeTab} />
            </div>
            
            <div className='mt-3'>
                {activeTab == 'Skogskador' && ListPanelData('Skogskador', 'Här nedan ser du resultatet från den senaste satellitövervakningen av din skog. Med hjälp av avancerad fjärranalys upptäcker vi förändringar i skogen – till exempel avverkning, stormskador eller andra avvikelser – så att du får full överblick utan att behöva gå ett enda steg.', 'Damage')}
                {activeTab == 'Risker' && ListPanelData('Risker', 'Som skogsägare är det avgörande att känna till aktuella risker som kan påverka din skog. Genom att hålla koll på aktuella risker får du bättre beslutsunderlag – och kan skydda både värdet och hälsan i din skog.', 'Risk')}
                {activeTab == 'I området' && ListPanelData('I området', 'Funktionen I området ger dig som skogsägare en snabb överblick över viktiga händelser i området kring din fastighet.', 'News')}
                
                {activeTab == 'Väder' && <WeatherDetails />}
                {activeTab == 'Vindanalys' && <WindAnalysis />}
            </div>
        </div>}
        
        <FeedbackModal show={showFeedback} setShow={setShowFeedback} />
    </>
})

export default Aragon;